import { smartSearch } from "../lib/utils";
import { useState, useEffect } from "react";
import { MainLayout } from "../components/Layout";
import { partsAPI } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Plus, Search, Edit, Trash2, Package, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  { value: "screen", label: "Écrans" },
  { value: "battery", label: "Batteries" },
  { value: "connector", label: "Connecteurs" },
  { value: "button", label: "Boutons" },
  { value: "camera", label: "Caméras" },
  { value: "speaker", label: "Haut-parleurs" },
  { value: "flex", label: "Nappes / Flex" },
  { value: "housing", label: "Châssis / Coques" },
  { value: "other", label: "Autres" },
];

export default function InventoryPage() {
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [brandFilter, setBrandFilter] = useState("all");
  const [groupByBrand, setGroupByBrand] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPart, setEditingPart] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    reference: "",
    category: "screen",
    brand: "Universel",
    model: "Universel",
    quantity: 0,
    unit_price: 0,
    min_stock: 5,
    supplier: "",
  });

  useEffect(() => {
    fetchParts();
  }, []);

  const fetchParts = async () => {
    try {
      const res = await partsAPI.list();
      setParts(res.data);
    } catch (err) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };
  
  // Get unique brands from parts
  const uniqueBrands = [...new Set(parts.map(p => p.brand || "Universel"))].sort();

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        quantity: parseInt(formData.quantity) || 0,
        unit_price: parseFloat(formData.unit_price) || 0,
        min_stock: parseInt(formData.min_stock) || 5,
      };
      
      if (editingPart) {
        await partsAPI.update(editingPart.id, data);
        toast.success("Pièce mise à jour");
      } else {
        await partsAPI.create(data);
        toast.success("Pièce ajoutée");
      }
      setDialogOpen(false);
      resetForm();
      fetchParts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const handleEdit = (part) => {
    setEditingPart(part);
    setFormData({
      name: part.name,
      reference: part.reference || "",
      category: part.category,
      quantity: part.quantity,
      unit_price: part.unit_price,
      min_stock: part.min_stock,
      supplier: part.supplier || "",
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer cette pièce ?")) return;
    try {
      await partsAPI.delete(id);
      toast.success("Pièce supprimée");
      fetchParts();
    } catch (err) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const resetForm = () => {
    setEditingPart(null);
    setFormData({
      name: "",
      reference: "",
      category: "screen",
      quantity: 0,
      unit_price: 0,
      min_stock: 5,
      supplier: "",
    });
  };

  const filteredParts = parts
    .filter((p) => {
      const matchesSearch = smartSearch(searchQuery, [p.name, p.reference, p.brand, p.model, p.category]);
      const matchesCategory = categoryFilter === "all" || p.category === categoryFilter;
      const matchesBrand = brandFilter === "all" || (p.brand || "Universel") === brandFilter;
      return matchesSearch && matchesCategory && matchesBrand;
    })
    .sort((a, b) => {
      // Sort by brand, then model, then name
      const brandA = (a.brand || "Universel").toLowerCase();
      const brandB = (b.brand || "Universel").toLowerCase();
      if (brandA !== brandB) return brandA.localeCompare(brandB);
      
      const modelA = (a.model || "Universel").toLowerCase();
      const modelB = (b.model || "Universel").toLowerCase();
      if (modelA !== modelB) return modelA.localeCompare(modelB);
      
      return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
    });
  
  // Group parts by brand for display
  const groupedParts = filteredParts.reduce((acc, part) => {
    const brand = part.brand || "Universel";
    if (!acc[brand]) acc[brand] = [];
    acc[brand].push(part);
    return acc;
  }, {});

  const lowStockParts = parts.filter(p => p.quantity <= p.min_stock);
  const getCategoryLabel = (value) => CATEGORIES.find(c => c.value === value)?.label || value;

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="inventory-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-chivo text-3xl font-bold text-foreground">Stock</h1>
            <p className="text-muted-foreground mt-1">{parts.length} pièce(s) en inventaire</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-part-btn" className="bg-primary hover:bg-primary/90 btn-animate">
                <Plus className="w-4 h-4 mr-2" />
                Nouvelle pièce
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-chivo">
                  {editingPart ? "Modifier la pièce" : "Nouvelle pièce"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Nom *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Écran iPhone 15 Pro"
                    required
                    data-testid="part-name-input"
                    className="bg-secondary/50"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Référence</Label>
                    <Input
                      value={formData.reference}
                      onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                      placeholder="REF-001"
                      data-testid="part-reference-input"
                      className="bg-secondary/50"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Catégorie *</Label>
                    <Select
                      value={formData.category}
                      onValueChange={(v) => setFormData({ ...formData, category: v })}
                    >
                      <SelectTrigger data-testid="part-category-select" className="bg-secondary/50">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map((c) => (
                          <SelectItem key={c.value} value={c.value}>
                            {c.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Quantité *</Label>
                    <Input
                      type="number"
                      min="0"
                      value={formData.quantity}
                      onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                      data-testid="part-quantity-input"
                      className="bg-secondary/50"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Prix unitaire (€) *</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.unit_price}
                      onChange={(e) => setFormData({ ...formData, unit_price: e.target.value })}
                      data-testid="part-price-input"
                      className="bg-secondary/50"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Stock min</Label>
                    <Input
                      type="number"
                      min="0"
                      value={formData.min_stock}
                      onChange={(e) => setFormData({ ...formData, min_stock: e.target.value })}
                      data-testid="part-min-stock-input"
                      className="bg-secondary/50"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Fournisseur</Label>
                  <Input
                    value={formData.supplier}
                    onChange={(e) => setFormData({ ...formData, supplier: e.target.value })}
                    placeholder="Nom du fournisseur"
                    data-testid="part-supplier-input"
                    className="bg-secondary/50"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }} className="flex-1">
                    Annuler
                  </Button>
                  <Button type="submit" data-testid="part-submit-btn" className="flex-1 bg-primary hover:bg-primary/90">
                    {editingPart ? "Mettre à jour" : "Ajouter"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Low Stock Alert */}
        {lowStockParts.length > 0 && (
          <Card className="bg-amber-500/10 border-amber-500/30">
            <CardContent className="p-4 flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              <div>
                <p className="font-medium text-foreground">Stock bas</p>
                <p className="text-sm text-muted-foreground">
                  {lowStockParts.length} pièce(s) à commander: {lowStockParts.map(p => p.name).join(", ")}
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 flex-wrap">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher une pièce..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="part-search-input"
              className="pl-10 bg-secondary/50"
            />
          </div>
          <Select value={brandFilter} onValueChange={setBrandFilter}>
            <SelectTrigger data-testid="brand-filter" className="w-40 bg-secondary/50">
              <SelectValue placeholder="Marque" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes les marques</SelectItem>
              {uniqueBrands.map((brand) => (
                <SelectItem key={brand} value={brand}>
                  {brand}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger data-testid="category-filter" className="w-48 bg-secondary/50">
              <SelectValue placeholder="Catégorie" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes les catégories</SelectItem>
              {CATEGORIES.map((c) => (
                <SelectItem key={c.value} value={c.value}>
                  {c.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant={groupByBrand ? "default" : "outline"}
            onClick={() => setGroupByBrand(!groupByBrand)}
            className="whitespace-nowrap"
          >
            {groupByBrand ? "Groupé par marque" : "Liste simple"}
          </Button>
        </div>

        {/* Parts Table */}
        {loading ? (
          <div className="text-center py-12 text-muted-foreground">Chargement...</div>
        ) : filteredParts.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>Aucune pièce trouvée</p>
          </div>
        ) : groupByBrand ? (
          /* Grouped view by brand */
          <div className="space-y-6">
            {Object.keys(groupedParts).sort().map((brand) => (
              <div key={brand} className="bg-card border border-border rounded-md overflow-hidden">
                <div className="bg-secondary/30 px-4 py-3 border-b border-border flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-foreground text-lg">{brand}</span>
                    <span className="text-sm text-muted-foreground">({groupedParts[brand].length} pièce(s))</span>
                  </div>
                  <span className={`text-sm px-2 py-1 rounded ${brand === 'Apple' ? 'bg-blue-500/20 text-blue-400' : brand === 'Samsung' ? 'bg-purple-500/20 text-purple-400' : 'bg-gray-500/20 text-gray-400'}`}>
                    {brand}
                  </span>
                </div>
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Pièce</th>
                        <th>Modèle</th>
                        <th>Référence</th>
                        <th>Catégorie</th>
                        <th>Stock</th>
                        <th>Prix</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {groupedParts[brand].map((part) => (
                        <tr key={part.id} data-testid={`part-row-${part.id}`}>
                          <td className="font-medium">{part.name}</td>
                          <td className="text-sm text-muted-foreground">{part.model || '-'}</td>
                          <td className="font-mono text-sm text-muted-foreground">{part.reference || '-'}</td>
                          <td>{getCategoryLabel(part.category)}</td>
                          <td>
                            <span className={`font-mono ${part.quantity <= part.min_stock ? 'text-amber-500' : 'text-foreground'}`}>
                              {part.quantity}
                            </span>
                            {part.quantity <= part.min_stock && (
                              <AlertTriangle className="w-4 h-4 text-amber-500 inline ml-2" />
                            )}
                          </td>
                          <td>{part.unit_price.toFixed(2)}€</td>
                          <td>
                            <div className="flex gap-1">
                              <button
                                onClick={() => handleEdit(part)}
                                className="p-2 hover:bg-secondary rounded-md transition-colors duration-150"
                                data-testid={`edit-part-${part.id}`}
                              >
                                <Edit className="w-4 h-4 text-muted-foreground" />
                              </button>
                              <button
                                onClick={() => handleDelete(part.id)}
                                className="p-2 hover:bg-destructive/10 rounded-md transition-colors duration-150"
                                data-testid={`delete-part-${part.id}`}
                              >
                                <Trash2 className="w-4 h-4 text-destructive" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* Simple list view */
          <div className="bg-card border border-border rounded-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Pièce</th>
                    <th>Marque</th>
                    <th>Modèle</th>
                    <th>Référence</th>
                    <th>Catégorie</th>
                    <th>Stock</th>
                    <th>Prix unitaire</th>
                    <th>Fournisseur</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredParts.map((part) => (
                    <tr key={part.id} data-testid={`part-row-${part.id}`}>
                      <td className="font-medium">{part.name}</td>
                      <td className="text-sm">{part.brand || 'Universel'}</td>
                      <td className="text-sm text-muted-foreground">{part.model || '-'}</td>
                      <td className="font-mono text-sm text-muted-foreground">{part.reference || '-'}</td>
                      <td>{getCategoryLabel(part.category)}</td>
                      <td>
                        <span className={`font-mono ${part.quantity <= part.min_stock ? 'text-amber-500' : 'text-foreground'}`}>
                          {part.quantity}
                        </span>
                        {part.quantity <= part.min_stock && (
                          <AlertTriangle className="w-4 h-4 text-amber-500 inline ml-2" />
                        )}
                      </td>
                      <td>{part.unit_price.toFixed(2)}€</td>
                      <td className="text-muted-foreground">{part.supplier || '-'}</td>
                      <td>
                        <div className="flex gap-1">
                          <button
                            onClick={() => handleEdit(part)}
                            className="p-2 hover:bg-secondary rounded-md transition-colors duration-150"
                            data-testid={`edit-part-${part.id}`}
                          >
                            <Edit className="w-4 h-4 text-muted-foreground" />
                          </button>
                          <button
                            onClick={() => handleDelete(part.id)}
                            className="p-2 hover:bg-destructive/10 rounded-md transition-colors duration-150"
                            data-testid={`delete-part-${part.id}`}
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
